import type { ObservableQuery } from '@apollo/client'
import { useApolloClient } from '@apollo/client/react'
import { getMainDefinition } from '@apollo/client/utilities'

import { useCollaborationNotesPanel } from '~publish/components/PostDialogs/PostDetailsContent/useCollaborationNotesPanel'
import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { ResultOf } from '~publish/graphql'
import { logError } from '~publish/legacy/utils/logError'
import { useBufferRealTimeSub } from '~publish/services/realtime'

import {
  insertCreatedNote,
  removeDeletedNote,
  replaceNoteInFeed,
} from './notesCache'
import { PostNoteTotal } from './fragments/PostNoteTotal'
import { PostUnreadMentionCount } from './fragments/PostUnreadMentionCount'
import { PostUnreadNoteCount } from './fragments/PostUnreadNoteCount'
import { CollaborationNoteWithActionsFields, NotesQuery } from './notesQueries'
import { postNoteSubject } from './noteSubject'

export const CollaborationNotesChanges = graphql(
  /* GraphQL */ `
    subscription CollaborationNotesChanges($organizationId: OrganizationId!) {
      collaborationNotesChanges(input: { organizationId: $organizationId }) {
        __typename
        ... on CollaborationNoteCreated {
          edge {
            cursor
            node {
              ...CollaborationNoteWithActionsFields
            }
          }
          # A create changes the total; the queue card's Notes button reads it.
          post {
            ...PostUnreadNoteCount
            ...PostUnreadMentionCount
            ...PostNoteTotal
          }
        }
        ... on CollaborationNoteUpdated {
          note {
            ...CollaborationNoteWithActionsFields
          }
          post {
            id
          }
        }
        ... on CollaborationNoteDeleted {
          noteId
          post {
            ...PostUnreadNoteCount
            ...PostUnreadMentionCount
            ...PostNoteTotal
          }
        }
      }
    }
  `,
  [
    CollaborationNoteWithActionsFields,
    PostUnreadNoteCount,
    PostUnreadMentionCount,
    PostNoteTotal,
  ],
)

type CollaborationNotesChange = ResultOf<
  typeof CollaborationNotesChanges
>['collaborationNotesChanges']

/**
 * Keeps every open Notes feed and every unread count live for the current
 * organization, through the same cache writes the Note mutations use.
 *
 * Mount this once: Apollo runs one stream for each mounted subscription. The
 * stream rides on the collaboration Notes flag.
 */
export function useLiveCollaborationNotes(): void {
  const organizationId = useOrganizationId()
  const { showPanel } = useCollaborationNotesPanel()
  const client = useApolloClient()

  useBufferRealTimeSub(CollaborationNotesChanges, {
    variables: { organizationId },
    skip: !showPanel,
    // Mounted for the whole session, so the default would refetch every
    // active query in Publish on each connect. Only a Notes feed can have
    // missed an event of this stream.
    refetchOnReconnect: readsNotesFeed,
    onData: (data): void => {
      applyChange(client.cache, data.collaborationNotesChanges)
    },
    onError: logError,
  })
}

const NOTES_FEED_QUERY_NAME = getMainDefinition(NotesQuery).name?.value

function readsNotesFeed(query: ObservableQuery): boolean {
  return query.queryName === NOTES_FEED_QUERY_NAME
}

function applyChange(
  cache: ReturnType<typeof useApolloClient>['cache'],
  change: CollaborationNotesChange,
): void {
  switch (change.__typename) {
    case 'CollaborationNoteCreated':
      insertCreatedNote(
        cache,
        postNoteSubject(change.post.id),
        change.edge,
        change.post.collaborationNotesSummary ?? undefined,
      )
      return
    // Fires for reactions as well as edits, to every subscriber in the
    // organization. Asking the server for the summary here would recount
    // Notes once per open tab on every reaction, for counts that a reaction
    // never changes. The next list load picks up a mention added by an edit.
    case 'CollaborationNoteUpdated':
      replaceNoteInFeed(cache, postNoteSubject(change.post.id), change.note)
      return
    case 'CollaborationNoteDeleted':
      removeDeletedNote(
        cache,
        postNoteSubject(change.post.id),
        change.noteId,
        change.post.collaborationNotesSummary ?? undefined,
      )
  }
}
